import { Logo } from '@/components/brand/Logo';

/**
 * Customer-facing error state — BUILD-PLAN §41.
 *
 * §41 is explicit that raw backend stack traces must never reach a customer,
 * so this component accepts only prose the brand has authored. Technical
 * detail belongs in the console and error monitoring, never on screen.
 */

interface ErrorStateProps {
  title?: string;
  description?: string;
  onRetry?: (() => void) | undefined;
  /** Use for whole-page failures; inline for a failed section. */
  fullScreen?: boolean;
}

export function ErrorState({
  title = 'Something went wrong',
  description = 'We were unable to complete that request. Please try again in a moment.',
  onRetry,
  fullScreen = false,
}: ErrorStateProps) {
  return (
    <div
      className={`flex flex-col items-center justify-center px-6 text-center ${
        fullScreen ? 'min-h-screen bg-background' : 'py-16'
      }`}
      role="alert"
    >
      {fullScreen && (
        <div className="mb-10">
          <Logo size="md" decorative />
        </div>
      )}

      <h2 className="font-display text-2xl text-ink">{title}</h2>

      <p className="mt-3 max-w-md text-sm leading-relaxed text-muted">
        {description}
      </p>

      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-8 inline-flex min-h-11 items-center justify-center rounded-sm border border-ink px-8 text-sm font-medium tracking-wide text-ink transition-colors hover:bg-ink hover:text-white"
        >
          Try Again
        </button>
      )}
    </div>
  );
}
